
import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./components/AuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import Navigation from "./components/Navigation";
import Dashboard from "./pages/Dashboard";
import Payments from "./pages/Payments";
import PaymentDetails from "./pages/PaymentDetails";
import PaymentCreation from "./pages/PaymentCreation";
import Readings from "./pages/Readings";
import ReadingDetails from "./pages/ReadingDetails";
import Expenses from "./pages/Expenses";
import HouseDetails from "./pages/HouseDetails";
import Profile from "./pages/Profile";
import Auth from "./pages/Auth";
import Calculator from "./pages/Calculator";
import Plot from "./pages/Plot";

const queryClient = new QueryClient();

const App = () => {
  return (
    <React.StrictMode>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <Dashboard />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <Profile />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payments"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <Payments />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payments/:id"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <PaymentDetails />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-creation"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <PaymentCreation />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/readings"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <Readings />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/readings/:id"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <ReadingDetails />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/expenses"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <Expenses />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/house"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <HouseDetails />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calculator"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <Calculator />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/plot"
                  element={
                    <ProtectedRoute>
                      <div className="flex flex-col md:flex-row min-h-screen w-full">
                        <Navigation />
                        <main className="flex-1 w-full pt-16 md:pt-0 md:pl-16">
                          <div className="max-w-[1600px] mx-auto p-4">
                            <Plot />
                          </div>
                        </main>
                      </div>
                    </ProtectedRoute>
                  }
                />
                {/* Catch all route - redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
};

export default App;
